import { useEffect, useMemo, useState } from 'react'
import { Building2, CornerDownLeft, Search } from 'lucide-react'
import { Button, Dialog, SearchField, StatePanel } from '../common'
import styles from './StudioAppShell.module.css'

function normalized(value) {
  return value.trim().toLocaleLowerCase()
}

export function CommandSearch({ destinations = [], organizations = [], onNavigate, onOrganizationSelect, enableShortcut = true }) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const term = normalized(query)
  const results = useMemo(() => {
    const commands = destinations.map((destination) => ({
      key: `route:${destination.key}`,
      type: 'route',
      label: destination.label,
      context: destination.productLabel,
      value: destination.path,
    }))
    const organizationCommands = organizations.map((organization) => ({
      key: `organization:${organization.slug}`,
      type: 'organization',
      label: organization.name || organization.slug,
      context: 'Organization',
      value: organization.slug,
    }))
    return [...commands, ...organizationCommands]
      .filter((command) => !term || `${command.label} ${command.context}`.toLocaleLowerCase().includes(term))
      .slice(0, 12)
  }, [destinations, organizations, term])

  useEffect(() => {
    if (!enableShortcut) return undefined
    const handleShortcut = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLocaleLowerCase() === 'k') {
        event.preventDefault()
        setOpen(true)
      }
    }
    window.addEventListener('keydown', handleShortcut)
    return () => window.removeEventListener('keydown', handleShortcut)
  }, [enableShortcut])

  const selectCommand = (command) => {
    setOpen(false)
    setQuery('')
    if (command.type === 'organization') onOrganizationSelect?.(command.value)
    else onNavigate?.(command.value)
  }

  return (
    <>
      <Button variant="secondary" size="compact" className={styles.commandTrigger} onClick={() => setOpen(true)}>
        <Search size={16} aria-hidden="true" />
        <span>Search Studio…</span>
        <kbd className={styles.shortcut}>⌘K</kbd>
      </Button>
      <Dialog
        open={open}
        title="Go to"
        description="Search Studio destinations and organizations."
        size="small"
        onClose={() => setOpen(false)}
      >
        <SearchField
          value={query}
          onChange={setQuery}
          label="Search Studio"
          placeholder="Search routes and organizations"
          inputProps={{ autoFocus: true }}
        />
        {results.length ? (
          <ul className={styles.commandResults} aria-label="Studio destinations">
            {results.map((command) => (
              <li key={command.key}>
                <button
                  type="button"
                  className={styles.commandResult}
                  onClick={() => selectCommand(command)}
                >
                  {command.type === 'organization' ? <Building2 size={16} aria-hidden="true" /> : <CornerDownLeft size={16} aria-hidden="true" />}
                  <span className={styles.commandCopy}>
                    <span>{command.label}</span>
                    <span>{command.context}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <div className={styles.commandResults}><StatePanel state="search-empty" title="No destinations match" compact /></div>
        )}
      </Dialog>
    </>
  )
}
